import { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { DemoModeBanner } from '@/components/DemoTour';
import KampagnenTrichterKarte from '@/components/KampagnenTrichterKarte';
import StarterpaketKarte from '@/components/StarterpaketKarte';
import { getActivityIcon } from '@/lib/activity-types';
import {
  Users, TrendingUp, Clock, AlertTriangle, ArrowUpRight, ArrowDownRight,
  CalendarCheck, ShoppingCart, UserPlus, ClipboardList, PhoneCall, Calendar,
  FileSignature, Activity, MessageSquare, Bookmark, Monitor, ExternalLink, Gift
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

function KPICard({ title, value, subtitle, icon: Icon, trend, color, onClick, highlight }) {
  return (
    <Card
      className={`cursor-pointer border-border/60 hover:border-primary/30 hover:shadow-md transition-all ${highlight ? 'ring-2 ring-red-200 border-red-300' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-[10px] sm:text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-xl sm:text-2xl font-bold tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                {trend > 0 ? <ArrowUpRight className="w-3 h-3 text-emerald-500" /> :
                 trend < 0 ? <ArrowDownRight className="w-3 h-3 text-red-500" /> : null}
                {subtitle}
              </p>
            )}
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (color || '#E60000') + '12' }}>
            <Icon className="w-4.5 h-4.5" style={{ color: color || '#E60000' }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const RANGE_OPTIONS = [
  { value: 'today', label: 'Heute' },
  { value: '7d', label: '7 Tage' },
  { value: '30d', label: '30 Tage' },
  { value: '90d', label: '90 Tage' },
  { value: 'all', label: 'Gesamt' },
];

const QUICK_ACTIONS = [
  { path: '/verkauf', icon: ShoppingCart, label: 'Neuer Verkauf', desc: 'Verkaufsrechner', color: '#E60000' },
  { path: '/leads?action=new', icon: UserPlus, label: 'Neuer Lead', desc: 'Lead anlegen', color: '#3B82F6' },
  { path: '/follow-ups', icon: PhoneCall, label: 'Follow-up', desc: 'Nachfassen', color: '#F59E0B' },
  { path: '/kalender', icon: Calendar, label: 'Termin', desc: 'Kalender öffnen', color: '#8B5CF6' },
  { path: '/vertraege', icon: FileSignature, label: 'Verträge', desc: 'Übersicht', color: '#10B981' },
];

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const canReception = isAdminPermissions || !!userPermissions?.['checkin.create'];
  const { startDemo } = useDemo();
  const { stageMap, formatDate } = useSettings();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await dashboardAPI.get({ time_range: range });
      setData(res.data);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-5 fade-in" data-testid="dashboard-loading">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-40 bg-muted animate-pulse rounded" />
            <div className="h-4 w-56 bg-muted animate-pulse rounded mt-2" />
          </div>
          <div className="h-8 w-64 bg-muted animate-pulse rounded-lg" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={`skeleton-${i}`} className="h-[88px] bg-card border border-border/40 animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={`qa-skeleton-${i}`} className="h-16 bg-card border border-border/40 animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="dashboard-error">
        <AlertTriangle className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground mb-3">Dashboard konnte nicht geladen werden.</p>
        <button onClick={() => { setLoading(true); fetchData(); }} className="text-xs font-medium text-primary hover:underline">
          Erneut versuchen
        </button>
      </div>
    );
  }

  const stageData = Object.entries(data.stage_counts).map(([key, value]) => ({
    name: stageMap[key]?.label || key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: stageMap[key]?.color || '#6B7280'
  }));

  const sourceData = (data.lead_sources || []).map((s, i) => ({
    name: s.source?.replace(/_/g, ' '),
    count: s.count,
    fill: ['#E60000', '#06B6D4', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#F97316'][i % 7]
  }));

  const roleLabel = !['admin', 'manager'].includes(data.user_role)
    ? "Deine persönliche Übersicht."
    : data.user_role === 'manager' ? "Team-Übersicht." : "Studio-Übersicht.";

  return (
    <div className="space-y-5 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            Willkommen, {user?.name?.split(' ')[0]}. <span className="hidden sm:inline">{roleLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1" data-testid="range-filter">
          {RANGE_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => { setLoading(true); setRange(opt.value); }} data-testid={`range-${opt.value}`}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${range === opt.value ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <DemoModeBanner onStart={startDemo} />

      {/* Empfang / Tablet-Modus — öffnet in neuem Tab (Kiosk-Ansicht fürs Tablet) */}
      {canReception && (
        <a
          href="/empfang"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="dashboard-empfang-link"
          className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Monitor className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Empfang / Tablet-Modus öffnen</p>
              <p className="text-xs text-muted-foreground">Check-in · Check-out · Auslastung · Verkaufsrechner — in neuem Tab</p>
            </div>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </a>
      )}

      {/* Schnellzugriff: Prämien & Shop */}
      {canReception && (
        <div className="grid grid-cols-2 gap-3" data-testid="dashboard-quicklinks">
          <button
            onClick={() => navigate('/praemien')}
            data-testid="dashboard-praemien-link"
            className="flex items-center gap-3 rounded-xl border border-border/60 bg-card hover:bg-muted/40 transition-colors px-4 py-3 text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">Prämien</p>
              <p className="text-xs text-muted-foreground truncate">Codes einlösen · Katalog verwalten</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/shop')}
            data-testid="dashboard-shop-link"
            className="flex items-center gap-3 rounded-xl border border-border/60 bg-card hover:bg-muted/40 transition-colors px-4 py-3 text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">Shop</p>
              <p className="text-xs text-muted-foreground truncate">Artikel für CRM & App verwalten</p>
            </div>
          </button>
        </div>
      )}

      {/* KPI Grid — Studio-Übersicht */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard title="Neue Leads" value={data.new_in_range || 0} subtitle={`${data.total_leads} gesamt`} icon={Users} trend={(data.new_in_range || 0) > 0 ? 1 : 0} color="#3B82F6" onClick={() => navigate('/leads')} />
        <KPICard title="Probetrainings" value={(data.probetrainings_week || 0) + (data.probetrainings_besucht_week || 0)}
          subtitle={`${data.probetrainings_week || 0} geplant · ${data.probetrainings_besucht_week || 0} besucht`}
          icon={CalendarCheck} trend={(data.probetrainings_besucht_week || 0) > 0 ? 1 : 0} color="#06B6D4"
          onClick={() => navigate('/probetrainings')} />
        <KPICard title="Abschlüsse" value={data.abschluesse_week || 0} subtitle="diese Woche" icon={TrendingUp} trend={(data.abschluesse_week || 0) > 0 ? 1 : 0} color="#22C55E"
          onClick={() => navigate('/leads?abschluss_status=abgeschlossen')} />
        <KPICard title="Nicht erschienen" value={data.no_shows_week || 0} subtitle={`${data.no_shows_total || 0} gesamt`}
          icon={AlertTriangle} trend={data.no_shows_week > 0 ? -1 : 1}
          color={data.no_shows_week > 0 ? '#EF4444' : '#10B981'}
          highlight={data.no_shows_week > 0}
          onClick={() => navigate('/leads?no_show=true')} />
      </div>

      {/* Row 2: Follow-ups + Verträge + Aufgaben */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard title="Offene Follow-ups" value={data.overdue_follow_ups}
          subtitle={data.overdue_follow_ups > 0 ? `${data.overdue_follow_ups} überfällig` : `${data.today_follow_ups} heute`}
          icon={data.overdue_follow_ups > 0 ? AlertTriangle : Clock}
          trend={data.overdue_follow_ups > 0 ? -1 : 1}
          color={data.overdue_follow_ups > 0 ? '#EF4444' : '#10B981'}
          highlight={data.overdue_follow_ups > 0}
          onClick={() => navigate('/follow-ups')} />
        <KPICard title="Verträge" value={data.total_vertraege || 0} subtitle={`${data.new_vertraege_range || 0} neu · ${data.aktive_vertraege || 0} aktiv`} icon={FileSignature} trend={(data.new_vertraege_range || 0) > 0 ? 1 : 0} color="#E60000" onClick={() => navigate('/vertraege')} />
        <KPICard title="Kinder & Jugend" value={data.kinder_count || 0} subtitle="Leads" icon={Users} color="#8B5CF6"
          onClick={() => navigate('/leads?bereich=kinder_jugend')} />
        <KPICard title="Erwachsene" value={data.erwachsene_count || 0} subtitle="Leads" icon={Users} color="#F59E0B"
          onClick={() => navigate('/leads?bereich=erwachsene')} />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {QUICK_ACTIONS.map(qa => (
          <button key={qa.path} onClick={() => navigate(qa.path)} data-testid={`qa-${qa.label.toLowerCase().replace(/\s/g,'-')}`}
            className="flex items-center gap-2.5 p-3 rounded-xl border border-border/60 bg-card hover:border-primary/20 hover:shadow-md transition-all text-left group">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: qa.color + '10' }}>
              <qa.icon className="w-4 h-4" style={{ color: qa.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{qa.label}</p>
              <p className="text-[9px] text-muted-foreground truncate">{qa.desc}</p>
            </div>
          </button>
        ))}
      </div>

      <StarterpaketKarte />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Pipeline Funnel */}
        <Card className="lg:col-span-5 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Pipeline-Status</CardTitle>
          </CardHeader>
          <CardContent>
            <PipelineFunnel stages={stageData} />
          </CardContent>
        </Card>

        {/* Trend */}
        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Neue Leads Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={data.trend_data}>
                <defs>
                  <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E60000" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#E60000" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} interval={Math.max(0, Math.floor((data.trend_data?.length || 7) / 7) - 1)} />
                <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                <Area type="monotone" dataKey="leads" stroke="#E60000" strokeWidth={2} fill="url(#leadGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Sources */}
        <Card className="lg:col-span-3 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Lead-Quellen</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={sourceData} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={2}>
                  {sourceData.map((entry, i) => <Cell key={entry.name || `source-${i}`} fill={entry.fill} />)}
                </Pie>
                <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {sourceData.slice(0, 4).map((s) => (
                <div key={s.name} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.fill }} />
                  <span className="text-[10px] text-muted-foreground capitalize truncate">{s.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity + Abschlüsse pro MA + Kampagnen */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Abschlüsse pro Mitarbeiter */}
        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Abschlüsse pro Mitarbeiter</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.abschluesse_pro_ma || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Noch keine Abschlüsse</p>
            ) : (
              <div className="space-y-2">
                {(data.abschluesse_pro_ma || []).map((ma, i) => (
                  <div key={`ma-${i}`} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{ma.name}</span>
                    <span className="text-sm font-bold text-primary">{ma.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kampagnen-Leistung */}
        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Kampagnen-Leistung</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.kampagnen_leistung || []).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">Keine Kampagnen-Daten</p>
            ) : (
              <div className="space-y-2">
                {(data.kampagnen_leistung || []).map((k, i) => (
                  <div key={`kamp-${i}`} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{k.kampagne?.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-bold">{k.leads} Leads</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Kinder vs Erwachsene */}
        <Card className="lg:col-span-4 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Bereiche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Kinder & Jugend</span>
                <span className="text-sm font-bold" style={{color: '#8B5CF6'}}>{data.kinder_count || 0}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="h-2 rounded-full" style={{width: `${Math.min(100, ((data.kinder_count || 0) / Math.max(1, (data.kinder_count || 0) + (data.erwachsene_count || 0))) * 100)}%`, backgroundColor: '#8B5CF6'}} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Erwachsene</span>
                <span className="text-sm font-bold" style={{color: '#F59E0B'}}>{data.erwachsene_count || 0}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="h-2 rounded-full" style={{width: `${Math.min(100, ((data.erwachsene_count || 0) / Math.max(1, (data.kinder_count || 0) + (data.erwachsene_count || 0))) * 100)}%`, backgroundColor: '#F59E0B'}} />
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-border/30">
                <span className="text-sm">Kampagnen</span>
                <span className="text-sm font-bold" style={{color: '#EC4899'}}>{data.kampagnen_count || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Herkunft nach UTM (First Touch am Lead: utm_source / utm_medium / utm_campaign) */}
      <Card className="border-border/60" data-testid="dashboard-herkunft">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Herkunft (UTM)</CardTitle>
        </CardHeader>
        <CardContent>
          {(data.herkunft_quellen || []).length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">Noch keine Leads mit UTM-Parametern im Zeitraum</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr><th className="text-left py-1 pr-3 font-medium">utm_source</th><th className="text-left py-1 pr-3 font-medium">utm_medium</th><th className="text-left py-1 pr-3 font-medium">utm_campaign</th><th className="text-left py-1 pr-3 font-medium">Kanal</th><th className="text-right py-1 font-medium">Leads</th></tr>
                </thead>
                <tbody>
                  {(data.herkunft_quellen || []).map((z, i) => (
                    <tr key={`hk-${i}`} className="border-t border-border/40 hover:bg-muted/40 cursor-pointer"
                        onClick={() => navigate(`/leads?quelle=${encodeURIComponent(z.quelle)}`)} title="Leads mit dieser Quelle anzeigen">
                      <td className="py-1 pr-3 font-medium">{z.quelle}</td>
                      <td className="py-1 pr-3">{z.medium || '–'}</td>
                      <td className="py-1 pr-3">{z.kampagne || '–'}</td>
                      <td className="py-1 pr-3 capitalize">{z.kanal || '–'}</td>
                      <td className="py-1 text-right font-bold text-primary">{z.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kampagnen-Trichter (26.09.2026): utm_campaign von Bio-Klick bis Vertrag, nur mit reports.view */}
      <KampagnenTrichterKarte />

      {/* Recent Activity */}
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Letzte Aktivitäten</CardTitle>
            <button onClick={() => navigate('/activities')} className="text-xs text-primary hover:underline font-medium" data-testid="view-all-activities">
              Alle anzeigen
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(data.recent_activities || []).map((act) => {
              const Icon = getActivityIcon(act.type);
              return (
                <div key={act.id} className="flex items-start gap-3 py-2 border-b border-border/30 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{act.created_by_name}</span>
                      <span className="text-muted-foreground"> — {act.type === 'anruf' ? 'Anruf' : act.type === 'email' ? 'E-Mail' : act.type === 'termin' ? 'Termin' : act.type === 'besuch' ? 'Besuch' : act.type === 'nachricht' ? 'Nachricht' : act.type === 'call' ? 'Anruf' : 'Notiz'} mit </span>
                      <span className="font-medium">{act.lead_name}</span>
                    </p>
                    {act.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate">{act.notes}</p>}
                  </div>
                  <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatDate(act.created_at)}</span>
                </div>
              );
            })}
            {(!data.recent_activities || data.recent_activities.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-6">Keine Aktivitäten vorhanden</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PipelineFunnel({ stages }) {
  if (!stages || stages.length === 0) return <p className="text-xs text-muted-foreground text-center py-8">Keine Pipeline-Daten</p>;
  const maxValue = Math.max(...stages.map(s => s.value), 1);
  return (
    <div className="space-y-1.5 py-1">
      {stages.map((stage) => {
        const widthPct = Math.max(20, (stage.value / maxValue) * 100);
        return (
          <div key={stage.name} className="flex items-center gap-3 group">
            <div className="w-20 text-right flex-shrink-0">
              <span className="text-[11px] text-muted-foreground">{stage.name}</span>
            </div>
            <div className="flex-1 relative">
              <div className="h-7 rounded-md flex items-center justify-between px-3 transition-all duration-500 group-hover:opacity-90"
                style={{ width: `${widthPct}%`, backgroundColor: stage.fill || '#6B7280', minWidth: '36px' }}>
                <span className="text-[11px] font-semibold text-white">{stage.value}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
